import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Renderer2, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Select, Store, Actions } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Observable, Subject } from 'rxjs';
import { BdGraph } from 'src/lib/graph/graph.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { FormControl } from '@angular/forms';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { filter, takeUntil, take, distinctUntilChanged } from 'rxjs/operators';

import * as _ from 'lodash';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { EngineService, EngineEventsEnum } from 'src/app/engine/engine.service';
import { LoadProgressModel } from 'src/app/engine/models/load-progress.model';

@Component({
  selector: 'app-navigation-context',
  templateUrl: './navigation-context.component.html',
  styleUrls: ['./navigation-context.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationContextComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('columnBody') 
  columnBody: ElementRef;

  @ViewChild('columnGroupBody')
  columnGroupBody: ElementRef;

  @ViewChild('searchInput')
  searchInput: ElementRef<HTMLInputElement>;

  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  @Select(StoryChaptersState.getSelectedChapter)
  currentChapter$: Observable<ChapterModel>;

  @Select(StoryChaptersState.getChaptersLoaded)
  chaptersLoaded$: Observable<any>;

  @Select(StoryChaptersState.getAllChaptersReady)
  allChaptersReady$: Observable<boolean>;

  public get selectedColumnGroup() {
    return this._selectedColumnGroup;
  }
  public set selectedColumnGroup(value) {
    this._selectedColumnGroup = value;
  }
  private _selectedColumnGroup = 'chapters';

  loadingLabel = '';

  interactive = false;
  searchHeight = 0;
  searchControl = new FormControl('');
  searchResults: ViewTreeNode[] = [];
  query = '';

  copies = [];

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private renderer: Renderer2,
    private changeDetector: ChangeDetectorRef,
    private engineEventsService: EngineService
  ) { }

  ngOnInit() {
    const _currentChapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
    if (_currentChapter && _currentChapter.readyState.model) {
      this.interactive = true;
    }

    this.searchControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(query => {
        this.query = query;
        this.searchResults = this.search(query);

        this.getSearchHeight()
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((height) => {
            this.searchHeight = height;
            this.changeDetector.markForCheck();
          });
      });

    this.engineEventsService.getEvent(EngineEventsEnum.AssetsLoadProgress)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: LoadProgressModel) => {
        const chaptersLoaded = this.store.selectSnapshot(StoryChaptersState.getChaptersLoaded);

        let loadingLabel = '';
        if (chaptersLoaded.count > 1) {
          loadingLabel = `Loading Chapter ${chaptersLoaded.modelChapters.length + 1} of ${chaptersLoaded.count}: `;
        }
        loadingLabel += `${Math.round(result.receivedAssets / result.requestedAssets * 100)}%`;
        this.loadingLabel = loadingLabel;
        this.changeDetector.markForCheck();
      });
      
    this.currentChapter$.pipe(
      filter((chapter) => chapter != null), 
      takeUntil(this.ngUnsubscribe)
    ).subscribe((chapter) => {
      this.interactive = chapter.readyState.model;
    });

    // When nodes are checked, flip to the the anatomy tree so
    // that the user can see what is checked
    this.store.select(EngineNodesState.getCheckedNodes)
      .pipe(
        filter(nodes => nodes.length > 0),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((nodes) => {
        this.selectedColumnGroup = 'anatomy';
        this.changeDetector.markForCheck();
      });
    
  }

  ngAfterViewInit() {
    // Window has resized, let the 3D canvas know
    this.renderer.listen('window', 'resize', (event) => {
      this.getSearchHeight()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((height) => {
          this.searchHeight = height;
          this.changeDetector.markForCheck();
        });
    });
  }

  ngOnDestroy() {
    this.changeDetector.detach();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleKeyup(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.clearQuery();
      this.searchInput.nativeElement.blur();
    }
  }

  getSearchHeight(): Observable<number> {
    return new Observable<number>((observer) => {
      const offsetHeight = _.get(this.columnGroupBody, 'nativeElement.parentElement.offsetHeight');
      if (offsetHeight != null) {
        observer.next(offsetHeight);
        observer.complete();
      }
    });
  }

  clearQuery() {
    this.searchControl.setValue('');
  }

  search(query: string): ViewTreeNode[] {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    query = query.toLowerCase();
    
    const results = Object.values(viewGraph.nodes)
      .filter((node: ViewTreeNode) => node.label.toLowerCase().indexOf(query) >= 0)
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      })
      .sort((a, b) => {
        return Math.sign(a.getDepth() - b.getDepth());
        
      });
    return results;
  }

  trackTreeNode(index: number, node: ViewTreeNode) { 
    return node.id; 
  }

}
